import React, { useRef, useState } from 'react';
import { ConfirmDialog as UIKitConfirmDialog, Spinner } from '@anyemp/ui-kit';
import { AlertTriangle, Trash2 } from 'lucide-react';
import useModalStackRegistration from '../../hooks/useModalStackRegistration';

/**
 * Compatibility adapter for existing Talent confirmation call sites.
 * UI Kit owns the dialog surface, actions, focus and dismissal behavior.
 */
const ConfirmDialog = ({
  isOpen,
  onClose,
  onConfirm,
  title = 'Confirm Action',
  message = 'Are you sure you want to proceed?',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmStyle = 'primary',
  icon: IconProp,
  iconColor,
  isLoading = false,
}) => {
  const confirmPendingRef = useRef(false);
  const [confirmPending, setConfirmPending] = useState(false);
  const isTopmost = useModalStackRegistration(isOpen);
  const isDanger = confirmStyle === 'danger';
  const confirmColor = isDanger ? 'error' : confirmStyle === 'warning' ? 'warning' : 'primary';
  const Icon = IconProp ?? (isDanger ? Trash2 : AlertTriangle);
  const resolvedIconColor = iconColor ?? (isDanger
    ? 'text-red-600 dark:text-red-400'
    : 'text-indigo-600 dark:text-indigo-400');

  const handleConfirm = async () => {
    if (confirmPendingRef.current || isLoading) return;
    confirmPendingRef.current = true;
    setConfirmPending(true);
    try {
      await onConfirm();
      onClose();
    } finally {
      confirmPendingRef.current = false;
      setConfirmPending(false);
    }
  };
  const pending = isLoading || confirmPending;

  return (
    <UIKitConfirmDialog
      open={isOpen}
      title={title}
      message={(
        <div className="flex min-w-0 items-start gap-3">
          <Icon className={`mt-0.5 h-5 w-5 flex-shrink-0 ${resolvedIconColor}`} aria-hidden="true" />
          <div className="min-w-0 flex-1">
            <div>{message}</div>
            {pending && (
              <div className="mt-3 flex items-center gap-2 text-sm text-light-text-tertiary dark:text-dark-text-tertiary">
                <Spinner size="small" color={isDanger ? 'error' : 'primary'} />
                Processing…
              </div>
            )}
          </div>
        </div>
      )}
      confirmLabel={pending ? 'Processing…' : confirmText}
      cancelLabel={cancelText}
      confirmColor={confirmColor}
      onConfirm={handleConfirm}
      onCancel={pending ? () => {} : onClose}
      closeOnBackdropClick={!pending && isTopmost}
      closeOnEscape={!pending && isTopmost}
      returnFocusOnClose
    />
  );
};

export default ConfirmDialog;
